import React from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

type Props = {
  open: boolean;
  onClose: () => void;
  instance: ActionLibraryInstance;
  onSuccess: () => void;
};

const ReopenInstanceDialog: React.FC<Props> = ({ open, onClose, instance, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [comment, setComment] = React.useState('');

  const reopenMutation = useMutation(
    (reopenComment: string) =>
      actionLibraryInstancesApi.reopenActionLibraryInstance(instance.id, {
        comment: reopenComment,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
        queryClient.invalidateQueries(['ACTION_LIBRARY_INSTANCE_STATUS_LOGS', instance.id]);
        enqueueSnackbar(t(action_library_messages.success.instance_reopened), { variant: 'success' });
        setComment('');
        onSuccess();
        onClose();
      },
      onError: () => {
        enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
      },
    },
  );

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const handleSubmit = () => {
    reopenMutation.mutate(comment.trim());
  };

  const commentTrimmed = comment.trim();
  const canSubmit = commentTrimmed.length > 0;

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.reopen_dialog_title)}</DialogTitle>
      <DialogContent>
        <TextField
          error={commentTrimmed.length === 0 && comment.length > 0}
          fullWidth
          helperText={
            commentTrimmed.length === 0 && comment.length > 0
              ? t(action_library_messages.instance_actions.reopen_dialog_comment_required)
              : undefined
          }
          label={t(action_library_messages.instance_actions.reopen_dialog_comment_placeholder)}
          multiline
          onChange={e => setComment(e.target.value)}
          required
          rows={3}
          value={comment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel[0], general_messages.cancel[1])}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={!canSubmit || reopenMutation.isLoading}
          icon={false}
          onClick={handleSubmit}
          showLoader={reopenMutation.isLoading}
          textLike={false}
          variant='contained'
        >
          {t(action_library_messages.instance_actions.reopen_instance)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReopenInstanceDialog;
